import { Routes, Route, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Layout from "./layout/Layout";
import RegisterPage from "./pages/register";
import LoginPage from "./pages/login";
import ProtectedRoute from "./components/ProtectedRoute";
import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import WrongPortalPage from "./pages/wrongPortal";
import NotFound from "../../../packages/ui/src/pages/notFound";
import {
  HUB_PUBLIC_ROUTES,
  HUB_PROTECTED_ROUTES,
  isPublicRoute,
  isNotFoundRoute,
} from "./routes";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setGlobalState, logout } from "./store/auth";
import { store } from "./store";
import { useQueryClient } from "@tanstack/react-query";

function App() {
  const location = useLocation();
  const auth = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const pendingAuthProcessedRef = useRef(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      if (navigator.onLine) {
        toast.success("You are back online!", {
          duration: 3000,
        });
      } else {
        toast.error("You are offline. Please check your internet connection.", {
          duration: 5000,
        });
      }
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  useEffect(() => {
    const handleAuthLogout = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("🚨 [APP] 401 LOGOUT EVENT DETECTED!", customEvent.detail);
      queryClient.cancelQueries();
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      store.dispatch(logout());
      auth?.removeUser();
      auth.signoutRedirect();
    };

    window.addEventListener("auth:logout", handleAuthLogout);
    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [auth, queryClient]);

  // Handle pending auth data from OIDC callback
  useEffect(() => {
    if (pendingAuthProcessedRef.current) {
      return;
    }

    const pendingAuthData = sessionStorage.getItem("pendingAuthData");

    if (pendingAuthData) {
      try {
        const authData = JSON.parse(pendingAuthData);
        
        if (!authData.userInfo?.subject_id) {
          console.error("❌ [APP] pendingAuthData has no subject_id - SKIPPING");
          sessionStorage.removeItem("pendingAuthData");
          pendingAuthProcessedRef.current = true;
          return;
        }
        
        if (authData.userInfo) {
          const userInfo = authData.userInfo;

          dispatch(
            setGlobalState({
              first_name: userInfo.first_name || "",
              last_name: userInfo.last_name || "",
              full_name: userInfo.full_name || "",
              email: userInfo.email || "",
              mobile: userInfo.mobile || null,
              subject_id: userInfo.subject_id || "",
              creation_date: userInfo.creation_date || null,
              last_accessed_branch_id: userInfo.last_accessed_branch_id || null,
              user_groups: userInfo.user_groups || [],
              user_permissions: userInfo.user_permissions || [],
              last_login: userInfo.last_login || "",
              user_branches: userInfo.user_branches
                ? userInfo.user_branches
                    .map((branch: { id: number; name: string }) => branch.id)
                    .join(",")
                : "",
              access_token: authData.access_token || "",
              id_token: authData.id_token || "",
              refresh_token: authData.refresh_token || "",
              expires_at: authData.expires_at || 0,
              tenant_id: authData.tenant_id || 0,
              storefront_id: authData.storefront_id || null,
              currency_code: authData.currency_code || null,
              exchange_rate: authData.exchange_rate || null,
            })
          );
          sessionStorage.removeItem("pendingAuthData");
          pendingAuthProcessedRef.current = true;
        }
      } catch (error) {
        console.error("Error parsing pending auth data:", error);
      }
    }
  }, [dispatch]);

  // Broadcast channel listener for cross-tab auth sync
  useEffect(() => {
    const authChannel = new BroadcastChannel("auth-sync");

    authChannel.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "LOGOUT":
          dispatch(logout());
          auth.removeUser();
          break;
        case "LOGIN":
          dispatch(setGlobalState(data));
          break;
        case "TOKEN_REFRESH":
          localStorage.setItem("token", data.access_token);
          break;
      }
    };

    return () => {
      authChannel.close();
    };
  }, [dispatch, auth]);

  // If current path is a public route - render without layout
  if (isPublicRoute(location.pathname) || isNotFoundRoute(location.pathname)) {
    return (
      <Routes>
        <Route path={HUB_PUBLIC_ROUTES.REGISTER} element={<RegisterPage />} />
        <Route path={HUB_PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
        <Route
          path={HUB_PUBLIC_ROUTES.WRONG_PORTAL}
          element={<WrongPortalPage />}
        />
        <Route path={HUB_PUBLIC_ROUTES.NOT_FOUND} element={<NotFound />} />
      </Routes>
    );
  }

  // Protected routes that need authentication - render with layout
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path={HUB_PROTECTED_ROUTES.HOME}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={HUB_PROTECTED_ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;

